(() => {
  'use strict';

  const ROOT = '[data-pqs-root]';
  const MODAL_ID = 'ProperQuickShopModal';
  let activeRoot = null;
  let activeProduct = null;
  let selectedVariant = null;
  let quantity = 1;
  let previousFocus = null;
  let busy = false;

  const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
  const money = cents => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: currency()
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  };

  const readProduct = root => {
    try { return JSON.parse(root.querySelector('[data-pqs-product]').textContent); }
    catch (error) { console.error('[PROPER Quick Shop] Invalid product JSON', error); return null; }
  };

  const sizeIndex = product => {
    if (Number.isInteger(product.size_index) && product.size_index >= 0) return product.size_index;
    return (product.options || []).findIndex(name => /size/i.test(name));
  };

  const getModal = () => document.getElementById(MODAL_ID);

  function setMessage(text, isError = false, desktop = false) {
    const node = desktop && activeRoot
      ? activeRoot.querySelector('[data-pqs-desktop-message]')
      : (getModal() && getModal().querySelector('[data-pqs-message]'));
    if (!node) return;
    node.textContent = text || '';
    node.dataset.error = isError ? 'true' : 'false';
  }

  function ensureModal() {
    let modal = getModal();
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'pqs-modal';
    modal.id = MODAL_ID;
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Quick add product');
    modal.innerHTML = `
      <div class="pqs-modal__backdrop" data-pqs-close></div>
      <div class="pqs-modal__panel" role="document">
        <div class="pqs-modal__handle" aria-hidden="true"></div>
        <button class="pqs-modal__close" type="button" aria-label="Close quick shop" data-pqs-close>&times;</button>
        <div class="pqs-modal__product">
          <img class="pqs-modal__image" data-pqs-image alt="" width="92" height="122">
          <div><p class="pqs-modal__title" data-pqs-title></p><p class="pqs-modal__price" data-pqs-price></p></div>
        </div>
        <div data-pqs-size-wrap>
          <p class="pqs-modal__label">Select size</p>
          <div class="pqs-modal__sizes" data-pqs-sizes></div>
        </div>
        <div class="pqs-modal__quantity">
          <span>Quantity</span>
          <div class="pqs-qty">
            <button type="button" data-pqs-qty-minus aria-label="Decrease quantity">−</button>
            <output data-pqs-qty>1</output>
            <button type="button" data-pqs-qty-plus aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="pqs-modal__atc" type="button" data-pqs-atc disabled>SELECT SIZE</button>
        <p class="pqs-modal__message" data-pqs-message role="status" aria-live="polite"></p>
      </div>`;
    document.body.appendChild(modal);
    return modal;
  }

  function availableVariants(product) {
    return (product.variants || []).filter(variant => variant.available);
  }

  function availableSizeLabels(product) {
    const index = sizeIndex(product);
    if (index < 0) return [];
    const labels = [];
    availableVariants(product).forEach(variant => {
      const label = variant.options[index];
      if (label != null && !labels.includes(label)) labels.push(label);
    });
    return labels;
  }

  function variantForSize(product, label) {
    const index = sizeIndex(product);
    if (index < 0) return availableVariants(product)[0] || null;
    // With colour swatches removed, use the first available variant for the chosen size.
    return availableVariants(product).find(variant => variant.options[index] === label) || null;
  }

  function updateDesktop(root, resetDefault = false) {
    const product = readProduct(root);
    if (!product) return;
    const sizes = root.querySelector('[data-pqs-desktop-sizes]');
    const atc = root.querySelector('[data-pqs-desktop-atc]');
    if (!sizes || !atc) return;

    const labels = availableSizeLabels(product);
    const soldOut = availableVariants(product).length === 0;
    const panel = root.querySelector('[data-pqs-desktop-panel]');
    if (panel) panel.dataset.pqsSoldOut = soldOut ? 'true' : 'false';

    if (resetDefault || !root.dataset.pqsVariant) {
      const first = labels.length ? variantForSize(product, labels[0]) : availableVariants(product)[0] || null;
      root.dataset.pqsVariant = first ? String(first.id) : '';
    }

    sizes.replaceChildren();
    if (sizeIndex(product) >= 0) {
      labels.forEach(label => {
        const variant = variantForSize(product, label);
        if (!variant) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pqs__size-button';
        button.textContent = label;
        button.setAttribute('aria-pressed', String(root.dataset.pqsVariant === String(variant.id)));
        button.addEventListener('click', () => {
          root.dataset.pqsVariant = String(variant.id);
          sizes.querySelectorAll('button').forEach(item => {
            const itemVariant = variantForSize(product, item.textContent);
            item.setAttribute('aria-pressed', String(itemVariant && String(itemVariant.id) === root.dataset.pqsVariant));
          });
          atc.disabled = false;
          atc.textContent = 'ADD TO CART';
          setMessage('', false, true);
        });
        sizes.appendChild(button);
      });
    }

    const selected = product.variants.find(v => String(v.id) === root.dataset.pqsVariant && v.available);
    atc.disabled = !selected || busy || soldOut;
    atc.textContent = busy ? 'ADDING…' : (soldOut ? 'SOLD OUT' : (sizeIndex(product) >= 0 && !selected ? 'SELECT SIZE' : 'ADD TO CART'));
  }

  function updateModalAtc() {
    const modal = getModal();
    if (!modal || !activeProduct) return;
    const button = modal.querySelector('[data-pqs-atc]');
    const anyAvailable = availableVariants(activeProduct).length > 0;
    const panel = modal.querySelector('.pqs-modal__panel');
    if (panel) panel.dataset.pqsSoldOut = anyAvailable ? 'false' : 'true';
    button.disabled = busy || !anyAvailable || !selectedVariant || !selectedVariant.available;
    button.textContent = busy ? 'ADDING…' : (!anyAvailable ? 'SOLD OUT' : (!selectedVariant ? 'SELECT SIZE' : 'ADD TO CART'));
  }

  function renderModalSizes() {
    const modal = getModal();
    const product = activeProduct;
    const wrapper = modal.querySelector('[data-pqs-size-wrap]');
    const container = modal.querySelector('[data-pqs-sizes]');
    container.replaceChildren();

    const index = sizeIndex(product);
    if (index < 0) {
      wrapper.hidden = true;
      selectedVariant = availableVariants(product)[0] || null;
      updateModalAtc();
      return;
    }

    wrapper.hidden = false;
    const labels = availableSizeLabels(product);
    if (labels.length === 0) {
      // If all variants are unavailable, show SOLD OUT and no empty size-button row.
      if (availableVariants(product).length === 0) wrapper.hidden = true;
    } else {
      wrapper.hidden = false;
    }
    labels.forEach(label => {
      const variant = variantForSize(product, label);
      if (!variant) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pqs-option';
      button.textContent = label;
      button.setAttribute('aria-pressed', String(selectedVariant && selectedVariant.id === variant.id));
      button.addEventListener('click', () => {
        selectedVariant = variant;
        container.querySelectorAll('button').forEach(item => {
          const itemVariant = variantForSize(product, item.textContent);
          item.setAttribute('aria-pressed', String(itemVariant && selectedVariant && itemVariant.id === selectedVariant.id));
        });
        modal.querySelector('[data-pqs-price]').textContent = money(variant.price);
        setMessage('');
        updateModalAtc();
      });
      container.appendChild(button);
    });

    // Preselect the first available size/variant by default.
    if (!selectedVariant || !selectedVariant.available) {
      selectedVariant = labels.length ? variantForSize(product, labels[0]) : null;
    }
    if (selectedVariant) {
      container.querySelectorAll('button').forEach(item => {
        const itemVariant = variantForSize(product, item.textContent);
        item.setAttribute('aria-pressed', String(itemVariant && itemVariant.id === selectedVariant.id));
      });
      modal.querySelector('[data-pqs-price]').textContent = money(selectedVariant.price);
    }
    updateModalAtc();
  }

  function openModal(root) {
    const product = readProduct(root);
    if (!product || !product.variants || !product.variants.length) return;
    activeRoot = root;
    activeProduct = product;
    selectedVariant = null;
    quantity = 1;
    previousFocus = document.activeElement;

    const modal = ensureModal();
    modal.querySelector('[data-pqs-title]').textContent = product.title || '';
    modal.querySelector('[data-pqs-price]').textContent = money(product.price);
    const image = modal.querySelector('[data-pqs-image]');
    image.src = product.featured_image || '';
    image.alt = product.title || '';
    modal.querySelector('[data-pqs-qty]').textContent = '1';
    setMessage('');
    renderModalSizes();
    modal.hidden = false;
    document.body.classList.add('pqs-modal-open');

    const trigger = root.querySelector('[data-pqs-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    modal.querySelector('[data-pqs-close]').focus();
  }

  function closeModal() {
    const modal = getModal();
    if (!modal || modal.hidden || busy) return;
    modal.hidden = true;
    document.body.classList.remove('pqs-modal-open');
    if (activeRoot) {
      const trigger = activeRoot.querySelector('[data-pqs-open]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    activeRoot = null;
    activeProduct = null;
    selectedVariant = null;
  }

  async function refreshCartAndOpen() {
    const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    try {
      const response = await fetch(`${rootPath}?sections=cart-drawer,cart-icon-bubble`, {
        headers: { 'Accept': 'application/json' }, credentials: 'same-origin'
      });
      if (response.ok) {
        const sections = await response.json();
        const targets = [
          ['cart-drawer', ['cart-drawer', '#CartDrawer', '[data-cart-drawer]']],
          ['cart-icon-bubble', ['#cart-icon-bubble', '[data-cart-count-bubble]']]
        ];
        targets.forEach(([key, selectors]) => {
          if (!sections[key]) return;
          const doc = new DOMParser().parseFromString(sections[key], 'text/html');
          selectors.some(selector => {
            const current = document.querySelector(selector);
            const fresh = doc.querySelector(selector);
            if (current && fresh) { current.replaceWith(fresh); return true; }
            return false;
          });
        });
      }
    } catch (error) {
      console.warn('[PROPER Quick Shop] Cart section refresh failed.', error);
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:change'));

    const drawer = document.querySelector('cart-drawer, #CartDrawer, [data-cart-drawer]');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else {
      const opener = document.querySelector('[aria-controls*="CartDrawer"], [data-cart-toggle], button[name="open-cart"], a[href="/cart"].header__icon');
      if (opener) opener.click();
    }
  }

  async function addVariant(variant, qty, desktop = false) {
    if (busy || !activeProduct || !variant || !variant.available) return;
    const modal = getModal();
    busy = true;
    if (desktop && activeRoot) {
      const button = activeRoot.querySelector('[data-pqs-desktop-atc]');
      if (button) { button.disabled = true; button.textContent = 'ADDING…'; }
    } else updateModalAtc();

    setMessage('', false, desktop);
    try {
      const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: qty }] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Could not add this item. Please try again.');
      await refreshCartAndOpen();
      if (!desktop) closeModal();
    } catch (error) {
      setMessage(error.message || 'Something went wrong. Please try again.', true, desktop);
      console.error('[PROPER Quick Shop] Add to cart failed:', error);
    } finally {
      busy = false;
      if (desktop && activeRoot) updateDesktop(activeRoot);
      if (!desktop && modal && !modal.hidden) updateModalAtc();
    }
  }

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-pqs-open]');
    if (opener) {
      event.preventDefault();
      const root = opener.closest(ROOT);
      if (root) openModal(root);
      return;
    }
    if (event.target.closest('[data-pqs-close]')) { closeModal(); return; }

    const desktopAtc = event.target.closest('[data-pqs-desktop-atc]');
    if (desktopAtc) {
      const root = desktopAtc.closest(ROOT);
      const product = root && readProduct(root);
      const variantId = root && root.dataset.pqsVariant;
      const variant = product && product.variants.find(v => String(v.id) === variantId && v.available);
      if (variant) { activeRoot = root; activeProduct = product; addVariant(variant, 1, true); }
      return;
    }

    const modal = getModal();
    if (!modal || modal.hidden) return;
    if (event.target.closest('[data-pqs-qty-minus]')) {
      quantity = Math.max(1, quantity - 1);
      modal.querySelector('[data-pqs-qty]').textContent = String(quantity);
    } else if (event.target.closest('[data-pqs-qty-plus]')) {
      quantity = Math.min(99, quantity + 1);
      modal.querySelector('[data-pqs-qty]').textContent = String(quantity);
    } else if (event.target.closest('[data-pqs-atc]')) {
      addVariant(selectedVariant, quantity, false);
    }
  });

  document.addEventListener('keydown', event => {
    const modal = getModal();
    if (!modal || modal.hidden) return;
    if (event.key === 'Escape') closeModal();
    if (event.key === 'Tab') {
      const focusable = [...modal.querySelectorAll('button:not(:disabled)')];
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  function initCards() {
    document.querySelectorAll(ROOT).forEach(root => {
      const card = root.closest('product-card, .product-card, .grid__item, .card-wrapper, li, article');
      if (card) card.setAttribute('data-pqs-card', '');
      updateDesktop(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCards);
  else initCards();
  document.addEventListener('shopify:section:load', initCards);
})();
